'use client';

import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { AnimatedButton } from './AnimatedButton';

export const EditorialCollageSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const centerY = useTransform(scrollYProgress, [0, 1], [90, -90]);
  const rightTopY = useTransform(scrollYProgress, [0, 1], [-95, 130]);
  const rightBottomY = useTransform(scrollYProgress, [0, 1], [120, -120]);
  const leftBottomY = useTransform(scrollYProgress, [0, 1], [95, -95]);
  const rightTopX = useTransform(scrollYProgress, [0, 1], [-18, 26]);
  const rightBottomX = useTransform(scrollYProgress, [0, 1], [24, -28]);
  const leftBottomX = useTransform(scrollYProgress, [0, 1], [-20, 22]);

  return (
    <section
      ref={sectionRef}
      id="signature-eleganze"
      className="relative w-full min-h-0 lg:min-h-[120vh] bg-white overflow-hidden py-8 md:py-8 lg:py-0"
    >
      {/* Decorative background letters */}
      <div className="absolute inset-0 hidden lg:flex items-center justify-center pointer-events-none z-0">
        <span className="w-full text-center text-[18vw] xl:text-[16vw] font-black tracking-[-0.05em] uppercase text-[#1d1d1d]/[0.08] leading-none select-none">
          Eleganze
        </span>
      </div>

      {/* Center hero image - desktop with parallax */}
      <div className="absolute inset-0 hidden lg:flex items-center justify-center pointer-events-none z-10">
        <motion.video
          style={{ y: centerY }}
          src="/images/video7.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="h-[72vh] w-[560px] xl:w-[620px] object-cover shadow-[0_24px_70px_rgba(0,0,0,0.32)]"
        />
      </div>

      {/* Left content block */}
      <div className="relative z-20 hidden lg:block">
        <div className="absolute top-28 left-10 xl:left-20 w-[320px] xl:w-[360px]">
          <h3 className="text-[#1d1d1d] text-xl font-bold tracking-[0.2em] mb-6 uppercase">Eleganze</h3>
          <h2 className="text-3xl xl:text-4xl font-black tracking-tight leading-[1.02] text-[#1d1d1d] mb-5">
            Signature Barbier
            <br />
            Eleganze
          </h2>
          <p className="text-gray-700 text-[15px] leading-relaxed mb-8 font-medium">
            À quelques minutes du centre, notre équipe de <strong>barbiers experts</strong> accompagne
            chaque client avec un style précis: coupe homme, dégradé moderne, taille de barbe et finitions
            premium. Eleganze combine exigence technique et confort pour devenir une adresse de référence
            autour de Poitiers.
          </p>
          <AnimatedButton
            href="#soins-coiffure"
            className="pointer-events-auto"
          >
            Voir les prestations
          </AnimatedButton>
        </div>
      </div>

      {/* Bottom-left image */}
      <div className="absolute bottom-0 left-0 hidden lg:flex items-end z-20 pointer-events-none">
        <motion.img
          style={{ y: leftBottomY, x: leftBottomX }}
          src="/images/experience.webp"
          alt="Ambiance premium et expérience soignée dans le salon Eleganze"
          loading="lazy"
          decoding="async"
          className="h-[36vh] xl:h-[44vh] w-[220px] xl:w-[290px] object-cover shadow-xl"
        />
      </div>

      {/* Right-side stacked images with parallax */}
      <div className="absolute top-16 right-28 xl:right-44 z-20 pointer-events-none hidden lg:block">
        <motion.img
          style={{ y: rightTopY, x: rightTopX }}
          src="/images/intemporelle.webp"
          alt="Style intemporel et finition élégante signés Eleganze"
          loading="lazy"
          decoding="async"
          className="w-48 xl:w-56 h-64 xl:h-80 object-cover shadow-xl border-4 border-white"
        />
      </div>

      <div className="absolute bottom-14 right-36 xl:right-64 z-20 pointer-events-none hidden lg:block">
        <motion.img
          style={{ y: rightBottomY, x: rightBottomX }}
          src="/images/savoir-faire.webp"
          alt="Savoir-faire barbier et gestes de précision chez Eleganze"
          loading="lazy"
          decoding="async"
          className="w-48 xl:w-56 h-64 xl:h-80 object-cover shadow-xl border-4 border-white"
        />
      </div>

      {/* Vertical side text */}
      <div className="absolute right-6 xl:right-10 top-1/2 -translate-y-1/2 z-20 pointer-events-none hidden lg:block">
        <div className="rotate-90 origin-center whitespace-nowrap text-[#1d1d1d] tracking-[0.3em] text-sm xl:text-base font-bold uppercase">
          Salon signature
        </div>
      </div>

      {/* Mobile + tablet adaptation: title + text + button only */}
      <div className="relative z-20 lg:hidden px-4 sm:px-6 pb-2 sm:pb-3">
        <div className="mx-auto max-w-3xl rounded-[2rem] border border-[#1d1d1d]/10 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-6 sm:p-8">
          <p className="text-xs font-bold tracking-[0.24em] uppercase text-gray-500 mb-3">Signature</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.05] text-[#1d1d1d] mb-4">
            Eleganze
            <br />
            Signature Barbier
          </h2>
          <p className="text-sm leading-relaxed text-gray-700 mb-6">
            Une équipe experte, un salon soigné et un accompagnement personnalisé à chaque rendez-vous.
            Coupe, barbe, épilation et finitions premium dans un même lieu.
          </p>
          <AnimatedButton
            href="#equipe"
            dark
          >
            Voir l'équipe
          </AnimatedButton>
        </div>
      </div>
    </section>
  );
};
